import React, { useEffect, useCallback, useState } from 'react';
import { useSelector } from 'react-redux';
import type { TableColumn } from 'react-data-table-component';
import CustomDataTable from '../CustomDataTable/CustomDataTable';
import MainLayout from '../Layouts/Mainlayout';
import Loader from '../Loader';
import { useSaveFilter } from '../../hooks/useSaveFilter';
import type {
  RootState,
  SavedReport,
  SavedReportsResponse,
  SharedUsersType,
} from '../types/types';
import type { TooltipProps } from '@mui/material';
import PeopleIcon from '@mui/icons-material/People';

// import CloseIcon from '@mui/icons-material/Close';
import { Avatar, AvatarGroup, Box, List, ListItem, ListItemText, Popover, styled, Tooltip, tooltipClasses } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import moment from 'moment';
import MUIModal from '../MUIModal/MUIModal';
import UserListModal from './UserListModal';

const isSavedReportsResponse = (
  value: unknown,
): value is SavedReportsResponse => {
  return (
    typeof value === 'object' &&
    value !== null &&
    'data' in value &&
    Array.isArray((value as SavedReportsResponse).data)
  );
};


interface UserDataProps {
  email: string;
  firstName: string;
  id: number;
  lastName: string;
}

const BootstrapTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip
    {...props}
    arrow
    classes={{ popper: className }}
    placement="bottom"
  />
))(({ theme }) => ({
  [`& .${tooltipClasses.arrow}`]: {
    color: theme.palette.common.black,
  },
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: theme.palette.common.black,
  },
}));

const BlackLink = styled(RouterLink)({
  color: 'black',
  textDecoration: 'underline',
  '&:hover': {
    color: 'black',
  },
});

const SavedReportList: React.FC = () => {
  const { user } = useSelector((state: RootState) => state.auth);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [openUserListModal, setOpenUserListModal] = useState<boolean>(false);
  const [selectedReportData, setSelectedReportData] = useState<SavedReport>()

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [openField, setOpenField] = useState<number | null>(null);

  const handleClick = (
    event: React.MouseEvent<HTMLElement>,
    field: number,
  ): void => {
    setAnchorEl(event.currentTarget);
    setOpenField(field);
  };

  const handleClose = (): void => {
    setAnchorEl(null);
    setOpenField(null);
  };

  const open = Boolean(anchorEl);

  const { savedReports, error } = useSelector(
    (state: RootState) => state.filter,
  );
  const { savedFilterReports } = useSaveFilter();

  const fetchReports = useCallback(async () => {
    if (user != null) {
      try {
        await savedFilterReports(user.id);
        setIsLoading(false);
      } catch (err) {
        setIsLoading(false);

        console.error('Error fetching saved reports:', err);
      }
    }
  }, [user, savedFilterReports]);

  useEffect(() => {
    void fetchReports();
  }, [fetchReports]);

  const handleShareToUserClick = (report: SavedReport): void => {
    setSelectedReportData(report)
    setOpenUserListModal(true)
  }

  const handleCloseShareToUser = (): void => {
    setOpenUserListModal(false)
  }

  const handleShareReport = (): void => {
    setOpenUserListModal(false)
    void fetchReports();
  }

  const columns: TableColumn<SavedReport>[] = [
    {
      name: (
        <BootstrapTooltip title="ReportName">
          <span>Report Name</span>
        </BootstrapTooltip>
      ),
      sortable: true,
      sortField: 'ReportName',
      cell: (row: SavedReport) => {
        const userData: string | null = localStorage.getItem("user");
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        const user: UserDataProps | undefined = userData !== null ? JSON.parse(userData) : undefined

        return (
          <div className="flex underline cursor-pointer">
            <BlackLink to={`/detailedsavedreport`} state={{ filterData: row.filterData, reportName: row.dashboardReportName }}>
              {row.dashboardReportName}
            </BlackLink>
            {user !== undefined && user.id !== row.userId ? <span style={{marginLeft: '15px'}}><PeopleIcon fontSize="small" /></span> : ''}
          </div>
        );
      },
    },
    {
      name: (
        <BootstrapTooltip title="Owner">
          <span>Owner</span>
        </BootstrapTooltip>
      ),
      sortable: true,
      sortField: 'ReportName',
      cell: (row: SavedReport) => {
        const fullName: string = row.createdFirstName + " " + row.createdLastName;
        return (
          <>
            <Avatar
              alt={fullName}
              src="/broken-image.jpg"
              sx={{ width: '30px', height: '30px', fontSize: '1rem', background: "#CBD1DC", color: "#3A3A3A" }}
            >{`${row.createdFirstName.charAt(0).toUpperCase()}${row.createdLastName !== null ? row.createdLastName.charAt(0).toUpperCase() : ''}`}</Avatar> <span style={{ paddingLeft: '5px' }}>{fullName}</span>
          </>
        );
      },
    },
    {
      name: (
        <BootstrapTooltip title="Shared">
          <span>Shared</span>
        </BootstrapTooltip>
      ),
      sortable: true,
      sortField: 'ReportName',
      cell: (row: SavedReport) => {
        return (
          <>
            <AvatarGroup >
              {
                row.sharedUsers.slice(0, 4)?.map(
                  (user: SharedUsersType) => {
                    
                    if (user.userId !== 0 && user.sharedFirstName !== null) {
                      const fullName: string = user.sharedFirstName + " " + user.sharedLastName;
                      return (
                        <BootstrapTooltip key={user.userId} title={fullName}><Avatar  alt={fullName} src="/broken-image.jpg" sx={{ width: '30px', height: '30px', fontSize: '1rem', background: "#CBD1DC", color: "#3A3A3A" }} >{`${user.sharedFirstName.charAt(0).toUpperCase()}${user.sharedLastName !== null ? user.sharedLastName.charAt(0).toUpperCase() : ''}`}</Avatar></BootstrapTooltip>
                      )
                    }
                  }

                )
              }
              {row.sharedUsers.length > 4 && <Avatar src="/broken-image.jpg" onClick={(event) => handleClick(event, row.dashboardFilterId)}>{`+${row.sharedUsers.length - 4}`}</Avatar> }
            </AvatarGroup>
            <Popover
              open={open && openField === row.dashboardFilterId}
              anchorEl={anchorEl}
              onClose={handleClose}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'left',
              }}
              PaperProps={{
                sx: {
                  maxHeight: 200,
                  overflowY: 'auto',
                  boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.1)',
                  borderRadius: '8px',
                  backgroundColor: '#fff',
                  mt: 1,
                  '&::-webkit-scrollbar': {
                    width: '6px',
                  },
                  '&::-webkit-scrollbar-thumb': {
                    backgroundColor: '#ED4B52',
                    borderRadius: '8px',
                    transition: 'background-color 0.3s',
                  },
                  '&::-webkit-scrollbar-thumb:hover': {
                    backgroundColor: '#C62828',
                  },
                  '&::-webkit-scrollbar-track': {
                    backgroundColor: '#f5f5f5',
                  },
                },
              }}
            >
              <Box>
                <List sx={{ padding: 0 }}>
                  {row.sharedUsers.map((value, index) => (
                    (value.userId !== 0 && value.sharedFirstName !== null) && <ListItem
                      key={`${value.userId}-${index}`}
                      sx={{
                        padding: '8px 16px',
                        '&:hover': {
                          backgroundColor: '#f5f5f5',
                        },
                        borderBottom:
                          index < row.sharedUsers.length - 1
                            ? '1px solid #e0e0e0'
                            : 'none',
                      }}
                    >
                      <ListItemText
                        primary={`${value.sharedFirstName + " " + value.sharedLastName}`}
                        sx={{
                          '& .MuiTypography-root': {
                            fontSize: '0.9rem',
                            color: '#4D5E71',
                          },
                        }}
                      />
                      
                    </ListItem>
                  ))}
                </List>
              </Box>
            </Popover>
            <img src='/assets/images/Users-Icon.svg' width={30} height={30} onClick={() => handleShareToUserClick(row)} style={{ cursor: 'pointer' }} />
          </>
        );
      },
    },
    {
      name: <BootstrapTooltip title='Created At'><span>Created On</span></BootstrapTooltip>,
      id: "createdOn",
      sortable: true,
      cell: (row: SavedReport) => {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call
        const createdOn = moment(row.createdOn).format('MM/DD/YYYY');
        return (
          <div className='flex w-full'>
            {createdOn}
          </div>
        )
      }
    }
  ];

  const reportsData: SavedReport[] = isSavedReportsResponse(savedReports)
    ? savedReports.data
    : [];
  console.log(reportsData);

  return (
    <div className="flex flex-row justify-start">
      <MainLayout>
        <div className="relative">
          <div className="flex justify-between items-center mb-[20px]">
            <div>
              <h1 className="text-2xl text-title font-semibold">
                Saved Reports
              </h1>
            </div>
          </div>
          <div
            className="mb-[30px] bg-white rounded-lg p-[20px] overflow-auto relative"
            style={{ minHeight: '500px' }}
          >
            {isLoading ? (
              <Loader />
            ) : error != null ? (
              <div className="text-red-500">{error}</div>
            ) : (
              <CustomDataTable
                columns={columns}
                data={reportsData}
                pagination
                responsive
                progressComponent={<Loader />}
              />
            )}
          </div>
        </div>
        <MUIModal openModal={openUserListModal} closeModal={handleCloseShareToUser} title='Share to user' width={'25%'} >
          <UserListModal reportData={selectedReportData} refreshPage={handleShareReport} onClose={handleCloseShareToUser}  />
        </MUIModal>
      </MainLayout>
    </div>
  );
};

export default SavedReportList;
